import { useState } from 'react';
import { ApiError, getErrorMessage } from '../api/client';
import { captureCountLine } from '../api/countSheets';
import type { AssignedCountLine } from '../types/countSheets';
import { countSumEcho, countTotal, emptyCountDraft, type CountDraft } from '../utils/inventoryCount';
import { randomUuid } from '../utils/randomUuid';
import { formatWasteQuantity } from '../utils/wasteReview';
import { Answers, FooterReason, Question } from './capture';
import { Modal } from './Modal';
import { useConfirm } from './ui/ConfirmDialog';

// F4 · contar A CIEGAS un insumo de la lista asignada. Se escribe lo que hay en el estante —suelto y/o
// por empaques, la pantalla solo SUMA— y se guarda. Nunca se muestra lo que dice el sistema ni la
// diferencia: eso lo ve el gerente al aprobar.

export function BlindCountModal({ line, storeName, onClose, onSaved }: {
  line: AssignedCountLine;
  storeName: string;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const confirm = useConfirm();
  const [draft, setDraftState] = useState<CountDraft>(() => (line.countedQuantity !== null ? { loose: line.countedQuantity, lines: [] } : emptyCountDraft()));
  // Una llave por lo escrito: reintentar lo MISMO (resultado en duda) no cuenta dos veces; cambiar lo
  // escrito es otra captura, con otra llave.
  const [requestId, setRequestId] = useState(() => randomUuid());
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string>();
  // «Cambiar» abre con lo que ya se contó: guardar sin tocar nada volvería a tomar la hora y lo que decía
  // el sistema de AHORA, como si se hubiera contado otra vez. Se pide cambiar algo antes.
  const [dirty, setDirty] = useState(false);
  const unchanged = line.countedQuantity !== null && !dirty;
  const presentations = line.presentations;
  const counted = countTotal(draft, presentations);
  const echo = countSumEcho(draft, presentations, line.unit.symbol);
  const hasPresentations = presentations.length > 0;

  const setDraft = (next: CountDraft) => {
    setDraftState(next);
    setRequestId(randomUuid());
    setError(undefined);
    setDirty(true);
  };
  const updateLine = (index: number, changes: Partial<CountDraft['lines'][number]>) => setDraft({ ...draft, lines: draft.lines.map((other, at) => (at === index ? { ...other, ...changes } : other)) });
  const close = () => { if (!isBusy) onClose(); };

  const save = async () => {
    if (isBusy || counted.total === null || unchanged) return;
    if (counted.total === 0 && !(await confirm({ title: '¿Contaste 0?', description: `Se guardará que no hay ${line.ingredient.name}.`, confirmLabel: 'Sí, no hay nada', cancelLabel: 'Revisar' }))) return;
    setIsBusy(true);
    setError(undefined);
    try {
      await captureCountLine(line.id, { requestId, countedQuantity: counted.total });
      onSaved(`Guardado: ${formatWasteQuantity(String(counted.total), line.unit.symbol)} de ${line.ingredient.name}.`);
    } catch (saveError) {
      if (saveError instanceof ApiError && saveError.status >= 400 && saveError.status < 500) {
        // No se guardó nada: el siguiente intento lleva otra llave.
        setError(getErrorMessage(saveError, 'No fue posible guardar lo contado.'));
        setRequestId(randomUuid());
      } else {
        setError('No sabemos si se guardó: se perdió la conexión o el servidor no respondió. Toca «Guardar» otra vez; si ya se había guardado, no se cuenta dos veces.');
      }
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Modal
      title={`Contar ${line.ingredient.name}`}
      description={`${storeName} · Escribe lo que ves en el estante`}
      isBusy={isBusy}
      onClose={close}
      footer={<>
        <FooterReason>{counted.isEmpty ? null : counted.problem ?? (unchanged ? 'Cambia la cantidad o toca «Cancelar».' : null)}</FooterReason>
        <button className="text-button" disabled={isBusy} onClick={close} type="button">Cancelar</button>
        <button className="button button--primary" disabled={isBusy || counted.total === null || unchanged} onClick={() => void save()} type="button">{isBusy ? 'Guardando…' : 'Guardar'}</button>
      </>}
    >
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {line.recount && <div className="alert alert--warning" role="status"><strong>Tu gerente pidió recontar.</strong>{line.recount.note ? ` ${line.recount.note}` : ''}</div>}
      <Question>¿Cuántos {line.unit.symbol} de {line.ingredient.name} hay?</Question>
      <Answers>
        <label className="field wiz-big-input">
          <span>{hasPresentations ? `Sueltos, en ${line.unit.name} (${line.unit.symbol})` : `Cantidad en ${line.unit.name} (${line.unit.symbol})`}</span>
          <input autoFocus enterKeyHint="done" inputMode="decimal" maxLength={14} onChange={(event) => setDraft({ ...draft, loose: event.target.value })} placeholder="0" type="text" value={draft.loose} />
        </label>
        {hasPresentations && <div aria-label="Contar por empaques" className="count-lines" role="group">
          {draft.lines.map((packageLine, index) => {
            const presentationName = presentations.find((presentation) => presentation.id === packageLine.presentationId)?.name ?? 'empaque';
            return (
              <div className="count-line" key={packageLine.id ?? index}>
                <label className="field">
                  <span>Empaque</span>
                  <select onChange={(event) => updateLine(index, { presentationId: event.target.value })} value={packageLine.presentationId}>
                    {presentations.map((presentation) => <option key={presentation.id} value={presentation.id}>{presentation.name} ({formatWasteQuantity(presentation.quantityInBaseUnit, line.unit.symbol)})</option>)}
                  </select>
                </label>
                <label className="field count-line__qty">
                  <span>¿Cuántos?</span>
                  <input inputMode="decimal" maxLength={10} onChange={(event) => updateLine(index, { packages: event.target.value })} placeholder="0" type="text" value={packageLine.packages} />
                </label>
                <button aria-label={`Quitar ${presentationName}`} className="text-button" onClick={() => setDraft({ ...draft, lines: draft.lines.filter((_, at) => at !== index) })} type="button">Quitar</button>
              </div>
            );
          })}
          <button className="link-button" onClick={() => setDraft({ ...draft, lines: [...draft.lines, { id: randomUuid(), presentationId: presentations[0]!.id, packages: '' }] })} type="button">+ Contar por empaques (cajas, bolsas…)</button>
        </div>}
        {echo && <p className="wiz-echo wiz-echo--big">{echo}</p>}
        <p className="wiz-hint">Cuenta todo lo que hay de este producto en la tienda. Tu gerente revisa el conteo antes de que cambie el inventario.</p>
      </Answers>
    </Modal>
  );
}
